import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Grid,
  ToggleButton, ToggleButtonGroup, Checkbox, FormControlLabel, RadioGroup, Radio, Chip,
  IconButton, Menu, Divider,
} from '@mui/material';
import {
  createTeamEvent, updateTeamEvent, listDivisionSiblingTeams, createTeamMatchup, updateTeamMatchup,
  completeTeamEvent, completeTeamMatchup, createRecurringEvent, updateRecurringSeries,
  postponeTeamEvent, postponeTeamMatchup, reopenTeamEvent, reopenTeamMatchup,
} from '../../services/scheduleService';
import { EVENT_TYPE_LABELS } from '../../utils/teamSchedule';
import { validateGameParticipants, buildGameParticipantPayload, resolveMatchupEditHomeAway, computeTeamPool, validateNeutralSiteLocation } from '../../utils/gameParticipants';
import { initEventScopeFromExisting, validateEventScope, resolveEventScopePayload } from '../../utils/eventScope';
import { WEEKDAY_LABELS, weekdayOfDateString, maxRecurrenceUntilDate } from '../../utils/recurrence';
import { parseTimeString, buildTimeString, minuteOptionsFor } from '../../utils/timeInput';

// Phase C.5c — tapping anywhere in a native date field (not just the
// tiny calendar-icon hit target) opens the browser's own date picker,
// where the browser supports the imperative `showPicker()` API (Chrome/
// Edge 99+). Wrapped in try/catch and optional-chained so it's a no-op,
// never an error, on a browser that lacks it — the field still works
// exactly as a normal native date input there (type/click-to-focus).
// No new date-picker dependency; the native input itself is preserved.
function openDatePicker(e) {
  try {
    e.currentTarget.querySelector('input')?.showPicker?.();
  } catch {
    // Unsupported browser/context — the native field still works normally.
  }
}

const EMPTY_TIME_PARTS = { hour12: '', minute: '', period: '' };

/** BROWSER ACCEPTANCE FIX (C.5c) — root cause of "Start time is
 * required" despite an apparent browser selection: the previous version
 * derived its displayed hour/minute/period FRESH from the external
 * `value` prop on every render, with no memory of its own between
 * clicks. `onChange` was only ever called once all three parts were
 * simultaneously present in that one derived object — but since picking
 * Hour alone never satisfied that (minute/period were still ''), the
 * function returned early without persisting the Hour pick ANYWHERE.
 * The next render re-derived from the still-unchanged `value` prop
 * (Hour lost), so picking Minute next started from scratch again, and a
 * fully composed time could only ever emit if a single onChange call
 * happened to already carry all three — which never happens with three
 * separate dropdowns. The field visually looked interactive while the
 * composed value it was supposed to be building was silently discarded
 * every single time.
 *
 * FIX — local state (`parts`) is now the single source of truth for
 * what's been picked so far, updated incrementally and persisting
 * between clicks; `onChange` still only fires once all three are
 * present (never a malformed value), but the accumulation actually
 * survives to that point. A `value`-prop-vs-last-emitted comparison
 * (not a naive `useEffect(() => setParts(...), [value])`, which would
 * also fire right after our OWN onChange and just be redundant) resyncs
 * `parts` whenever the value changes for a reason OTHER than this
 * field's own onChange — e.g. the surrounding dialog's own reset effect
 * loading a different existing event's stored time, which — because
 * that effect runs one render after the Dialog itself mounts open="true"
 * — can hand this field a corrected `value` prop slightly after this
 * component's own first render.
 *
 * PRESENTATION — also addresses the accepted product feedback that
 * three small disconnected boxes ("Hr · Min · —") were unreadable at a
 * glance: the resting control is now ONE field showing the complete
 * human-readable time ("5:00 PM"); tapping it opens a compact MUI Menu
 * (no new dependency) containing the same Hour/Minute/AM-PM selects,
 * which auto-closes the instant a complete time is chosen. Still emits
 * (and accepts) the exact same 24-hour 'HH:MM' string every RPC already
 * expects — see utils/timeInput.js for the pure conversion helpers. */
export function TimeSelectField({ label, value, onChange, required }) {
  const [parts, setParts] = useState(() => parseTimeString(value) || EMPTY_TIME_PARTS);
  const [anchorEl, setAnchorEl] = useState(null);
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setParts(parseTimeString(value) || EMPTY_TIME_PARTS);
    }
  }, [value]);

  const minuteOptions = minuteOptionsFor(parts.minute === '' ? null : parts.minute);
  const isComplete = parts.hour12 !== '' && parts.minute !== '' && parts.period !== '';
  const displayText = isComplete ? `${parts.hour12}:${String(parts.minute).padStart(2, '0')} ${parts.period}` : '';

  const applyPatch = (patch) => {
    const next = { ...parts, ...patch };
    setParts(next);
    if (next.hour12 !== '' && next.minute !== '' && next.period !== '') {
      const built = buildTimeString(next);
      lastEmitted.current = built;
      onChange(built);
      setAnchorEl(null);
    }
  };

  return (
    <Box>
      <TextField
        fullWidth label={label} required={required} value={displayText} placeholder="Select time"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ '& input': { cursor: 'pointer' } }}
        InputProps={{ readOnly: true, endAdornment: <Box component="span" sx={{ color: '#6a8aa8', ml: 0.5 }}>▾</Box> }}
      />
      <Menu
        anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a', p: 1.5 } }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, minWidth: 220 }}>
          <TextField
            select label="Hour" value={parts.hour12} onChange={(e) => applyPatch({ hour12: Number(e.target.value) })}
            SelectProps={{ displayEmpty: true }} sx={{ minWidth: 68, flex: 1 }}
          >
            {parts.hour12 === '' && <MenuItem value="" disabled>Hr</MenuItem>}
            {Array.from({ length: 12 }, (_, i) => i + 1).map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
          </TextField>
          <TextField
            select label="Minute" value={parts.minute} onChange={(e) => applyPatch({ minute: Number(e.target.value) })}
            SelectProps={{ displayEmpty: true }} sx={{ minWidth: 68, flex: 1 }}
          >
            {parts.minute === '' && <MenuItem value="" disabled>Min</MenuItem>}
            {minuteOptions.map(m => <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}</MenuItem>)}
          </TextField>
          <TextField
            select label="AM/PM" value={parts.period} onChange={(e) => applyPatch({ period: e.target.value })}
            SelectProps={{ displayEmpty: true }} sx={{ minWidth: 76, flex: 1 }}
          >
            {parts.period === '' && <MenuItem value="" disabled>—</MenuItem>}
            <MenuItem value="AM">AM</MenuItem>
            <MenuItem value="PM">PM</MenuItem>
          </TextField>
        </Box>
      </Menu>
    </Box>
  );
}

// Phase C.4a — Add/Edit Event and Mark Completed, shared by
// TeamSchedulePage.js and the Division Hub (DivisionSchedulePage.js).
// Every item these dialogs operate on carries `kind` ('event' |
// 'matchup') and `ownerTeamId` — set by whichever page merged
// team_events/listTeamMatchups() (042) into its own display list.
//
// GAME PARTICIPANT IDENTITY (this pass) — a Game's two participants are
// each a "slot": { kind: 'team', teamId, name } for a known Division
// Team (never free text, never name-matched — an actual Team ID) or
// { kind: 'external', name } for an Outside Program opponent (still
// free text this pass — a reusable Opponent directory is a separate,
// not-yet-built concept; see this pass's own architecture report).
// Home/Away is which SLOT a participant sits in, not a raw stored
// enum value — this file is the only place that translates slot
// position into `team_a_home`/`team_b_home`/`away`/`neutral` for the
// two different underlying RPC families (team_matchups vs team_events).

const EMPTY_FORM = {
  eventType: 'practice', title: '', eventDate: '', startTime: '', endTime: '',
  locationName: '', addressLine1: '', city: '', state: '', zipCode: '', notes: '',
};

function eventToForm(event) {
  return {
    eventType: event.event_type,
    title: event.title,
    eventDate: event.event_date,
    startTime: (event.start_time || '').slice(0, 5),
    endTime: (event.end_time || '').slice(0, 5),
    locationName: event.location_name || '',
    addressLine1: event.address_line1 || '',
    city: event.city || '',
    state: event.state || '',
    zipCode: event.zip_code || '',
    notes: event.notes || '',
  };
}

/** Editing an existing internal matchup only ever changes date/start
 * time/site/notes (042/045's own update_team_matchup() never accepts
 * different Teams, and never accepts end time or location — a matchup
 * still doesn't model a venue at all) — everything else in EMPTY_FORM is
 * simply unused for this branch. `notes` (045) is preloaded here so
 * Edit shows the actual stored value, exactly like eventToForm()'s own
 * notes handling for team_events. */
export function matchupToForm(matchup) {
  return {
    ...EMPTY_FORM, eventType: 'game', eventDate: matchup.event_date, startTime: (matchup.start_time || '').slice(0, 5),
    notes: matchup.notes || '',
  };
}

/** One participant slot in the Home Team / Away Team pair. `kind:
 * 'team'` is a known Division Team — never rendered as free text, and
 * the ONLY way `teamId` is ever set is by picking it from
 * `teamPoolOptions`, which is always built from real Team rows (never
 * from typed text, never matched by name). `kind: 'external'` is an
 * Outside Program opponent, still a plain name this pass.
 *
 * `lockIdentity` — true whenever WHO is playing can never change
 * (editing an existing Game: neither update_team_matchup() nor
 * update_team_event() can reassign participants). A locked `team` slot
 * renders as fixed text; a locked `external` slot still allows
 * CORRECTING the opponent's name (update_team_event() already accepts a
 * new opponent_name) — only its identity-kind is frozen, not its text. */
function ParticipantSlot({ slot, onChange, teamPoolOptions, allowExternal, lockIdentity }) {
  if (lockIdentity) {
    if (!slot) return null;
    if (slot.kind === 'team') {
      return <TextField fullWidth value={slot.name || ''} disabled />;
    }
    return <TextField fullWidth value={slot.name} onChange={e => onChange({ ...slot, name: e.target.value })} required />;
  }
  const value = slot ? (slot.kind === 'team' ? slot.teamId : '__external__') : '';
  return (
    <>
      <TextField
        select fullWidth value={value}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '__external__') { onChange({ kind: 'external', name: '' }); return; }
          const picked = teamPoolOptions.find(t => t.id === v);
          onChange({ kind: 'team', teamId: v, name: picked?.team_name || '' });
        }}
        // Phase C.5c — the closed field stays fullWidth (room for a
        // realistic Team name), but the OPEN menu is capped rather than
        // stretching to match a wide dialog for what's usually only 3-5
        // options; touch targets (MenuItem height) are left at their
        // default size, never shrunk.
        SelectProps={{ MenuProps: { PaperProps: { sx: { maxWidth: 320 } } } }}
      >
        {teamPoolOptions.map(t => <MenuItem key={t.id} value={t.id}>{t.team_name}</MenuItem>)}
        {allowExternal && <MenuItem value="__external__">+ Outside Program</MenuItem>}
      </TextField>
      {slot?.kind === 'external' && (
        <TextField fullWidth sx={{ mt: 1 }} placeholder="Opponent name" value={slot.name} onChange={e => onChange({ ...slot, name: e.target.value })} required />
      )}
    </>
  );
}

/** Create/Edit Event — one form, reused for team_events (external games/
 * practice/other) AND team_matchups (internal FFN-vs-FFN games).
 *
 * `teamId` — fixed owning Team (TeamSchedulePage's own usage).
 *
 * `teamOptions` — every Team available in this context: the full
 * Division roster from the Division Hub (length > 1), or just "my own
 * Team" from TeamSchedulePage (length === 1, in which case this dialog
 * fetches that Team's own Division siblings itself to build out the
 * rest of the participant pool — a Team page already knows "my Team,"
 * it should never ask again).
 *
 * `event` (existing item) carries `kind: 'event' | 'matchup'` and
 * `ownerTeamId`, and — for a Practice/Other event — `scope_type`/
 * `scope_team_ids` (043) when editing.
 *
 * `divisionId` — required to create/edit an Entire-Division event from
 * the Division Hub; unused (and the Entire-Division/Specific-Teams
 * choice never even renders) from TeamSchedulePage, which only ever
 * knows "my Team" — see EVENT SCOPE below. */
export function EventFormDialog({ open, onClose, onSaved, teamId, teamOptions, divisionId, event }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [siblingTeams, setSiblingTeams] = useState([]);
  const [showAddressDetails, setShowAddressDetails] = useState(false);
  // Game-only participant state — see ParticipantSlot's own header for
  // the exact shape. Unused for Practice/Other, which use EVENT SCOPE
  // below instead.
  const [homeSlot, setHomeSlot] = useState(null);
  const [awaySlot, setAwaySlot] = useState(null);
  const [neutralSite, setNeutralSite] = useState(false);
  // Phase C.5c — Neutral Site UX. A neutral-site game still needs a
  // venue; this doesn't build reusable Location storage (that's a
  // separate, later phase) — it's the simplest honest V1: either a real
  // typed Location, or an explicit acknowledgment that it isn't known
  // yet, stored as the plain text "TBD" in the same location_name field
  // that already exists (no schema change). Irrelevant for an internal
  // Team-vs-Team matchup, which has no location field at all.
  const [locationTBD, setLocationTBD] = useState(false);
  // EVENT SCOPE (043) — Practice/Other only. 'team' | 'entire_division' |
  // 'specific_teams'. Only ever choosable from a multi-Team context (the
  // Division Hub); a single-Team context (TeamSchedulePage, or a
  // one-Team Division) has no "Applies To" choice at all and stays
  // 'team' — it already knows the only possible answer.
  const [applyScope, setApplyScope] = useState('team');
  const [specificTeamIds, setSpecificTeamIds] = useState([]);
  // Phase C.5b — recurrence. repeatEnabled/repeatWeekdays/repeatUntilDate
  // drive CREATING a new recurring series (Practice/Other only).
  // applyTo/changePattern drive EDITING an existing occurrence that
  // already belongs to a series — "This occurrence only" (the safe
  // default) never touches recurrence at all; "This and future"/"Entire
  // series" may also change the weekday pattern itself via the SAME
  // repeatWeekdays state, opted into via changePattern (unchecked by
  // default — most edits are a simple field change, not a pattern
  // change, and a pattern change is always a series split, see 044).
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [repeatWeekdays, setRepeatWeekdays] = useState([]);
  const [repeatUntilDate, setRepeatUntilDate] = useState('');
  const [applyTo, setApplyTo] = useState('this');
  const [changePattern, setChangePattern] = useState(false);
  // Phase C.5c — End Time stays collapsed behind "+ Add End Time" for a
  // fresh/no-end-time event (reducing clutter, per product feedback);
  // editing an event that already has one shows the field immediately —
  // see the reset effect below, where this is set from the actual event.
  const [endTimeExpanded, setEndTimeExpanded] = useState(false);

  const isEditingMatchup = event?.kind === 'matchup';
  const isCreating = !event;
  const isRecurringOccurrence = Boolean(event && !isEditingMatchup && event.series_id);
  // Only ever the fixed, single-Team context now — a multi-Team context
  // resolves Practice/Other ownership entirely through EVENT SCOPE
  // (applyScope/specificTeamIds) below instead of a Team picker.
  const effectiveTeamId = teamId;
  const isMultiTeamContext = Array.isArray(teamOptions) && teamOptions.length > 1;
  // Phase C.5a — "Applies To" replaces the old single-Team selector for
  // Practice/Other in a multi-Team context: Entire Division, or an
  // explicit set of Teams (which may be just one — a real specific_
  // teams membership, not a pretend "the Team scope"). Available on
  // both create AND edit (043's update_team_event() supports a valid
  // scope transition) — but only ever in a multi-Team context; a
  // single-Team context (TeamSchedulePage, or a one-Team Division)
  // never shows this at all and stays 'team' — see TeamSchedulePage's
  // own decision in this pass's report for why broader-scope creation
  // stays Division-Hub-only.
  // Scope changes are only ever a "this + future"/"entire series"
  // concept (044) — never exposed while editing just one occurrence, so
  // a series' own participation always has exactly one source of truth.
  const showApplyScope = form.eventType !== 'game' && isMultiTeamContext && (!isRecurringOccurrence || applyTo !== 'this');
  const isInternalGame = form.eventType === 'game' && homeSlot?.kind === 'team' && awaySlot?.kind === 'team';
  // Phase C.5c — "Starts Sep 15 / Repeats Weekly / On Tue/Thu / Until
  // Oct 15" should read naturally; the field stays plain "Date" any time
  // there's no recurrence in play at all (editing never shows this
  // relabel — an existing occurrence's own Date is never a series
  // "start," even one belonging to a series, since "this occurrence
  // only" edits a single date, not the series' anchor).
  const dateFieldLabel = (form.eventType !== 'game' && isCreating && repeatEnabled) ? 'Starts' : 'Date';
  // The one Division Team a single-Team context (TeamSchedulePage) is
  // scoped to, so the sibling fetch below knows whose Division to look
  // in without a second selector — a primitive, not the `teamOptions`
  // array itself, so this doesn't refire on every unrelated re-render
  // of a caller that builds that array inline.
  const soloTeamId = (Array.isArray(teamOptions) && teamOptions.length === 1) ? teamOptions[0].id : null;
  // The full Home/Away participant pool: the Division Hub already hands
  // over every Team in the Division (teamOptions.length > 1); a single-
  // Team context merges in that Team's own fetched Division siblings.
  const teamPool = computeTeamPool(teamOptions, siblingTeams);

  useEffect(() => {
    if (!open) return;
    setForm(isEditingMatchup ? matchupToForm(event) : (event ? eventToForm(event) : EMPTY_FORM));
    setError(null);
    setShowAddressDetails(Boolean(event?.address_line1 || event?.city || event?.state || event?.zip_code));
    setEndTimeExpanded(Boolean(!isEditingMatchup && event?.end_time));

    if (isEditingMatchup) {
      // Only date/time/site are ever editable — participants are fixed,
      // resolved from whichever Team's own perspective this row was
      // read from (ownerTeamId) plus the already perspective-normalized
      // opponent fields list_team_matchups()/list_public_division_games()
      // (via perspectiveFromDivisionGame()) already provide.
      const ownerName = event.ownerTeamName || (teamOptions || []).find(t => t.id === event.ownerTeamId)?.team_name || 'Your Team';
      const ownerSlot = { kind: 'team', teamId: event.ownerTeamId, name: ownerName };
      const opponentSlot = { kind: 'team', teamId: event.opponent_team_id, name: event.opponent_name };
      const ownerIsHome = event.home_away !== 'away';
      setHomeSlot(ownerIsHome ? ownerSlot : opponentSlot);
      setAwaySlot(ownerIsHome ? opponentSlot : ownerSlot);
      setNeutralSite(event.home_away === 'neutral');
      setLocationTBD(false); // an internal matchup has no location field at all
    } else if (event && event.event_type === 'game') {
      // Editing an existing EXTERNAL game — the in-house side is fixed;
      // the Outside Program name can still be corrected.
      const ownerName = event.ownerTeamName || (teamOptions || []).find(t => t.id === event.ownerTeamId)?.team_name || 'Your Team';
      const ownerSlot = { kind: 'team', teamId: event.ownerTeamId, name: ownerName };
      const externalSlot = { kind: 'external', name: event.opponent_name || '' };
      const ownerIsHome = event.home_away !== 'away';
      setHomeSlot(ownerIsHome ? ownerSlot : externalSlot);
      setAwaySlot(ownerIsHome ? externalSlot : ownerSlot);
      setNeutralSite(event.home_away === 'neutral');
      setLocationTBD(event.location_name === 'TBD');
    } else {
      // Creating fresh (or editing a Practice/Other, where Game slots
      // are unused). A single-Team context pre-fills "my Team" into
      // Home — it already knows the answer, so it never asks. A
      // multi-Team Division Hub starts both slots empty; there's no
      // canonical "my Team" to assume there.
      setHomeSlot(soloTeamId ? { kind: 'team', teamId: soloTeamId, name: teamOptions[0].team_name } : null);
      setAwaySlot(null);
      setNeutralSite(false);
      setLocationTBD(false);

      // EVENT SCOPE (043) — Practice/Other. Editing an existing event
      // prefills its actual current scope; creating fresh in a multi-
      // Team context defaults to Entire Division (the more common case,
      // and the friction-removing default the Division Hub asks for).
      // A single-Team context has nothing to choose — always 'team'.
      if (event) {
        const initial = initEventScopeFromExisting(event);
        setApplyScope(initial.applyScope);
        setSpecificTeamIds(initial.specificTeamIds);
      } else if (Array.isArray(teamOptions) && teamOptions.length > 1) {
        setApplyScope('entire_division');
        setSpecificTeamIds([]);
      } else {
        setApplyScope('team');
        setSpecificTeamIds([]);
      }
    }

    // Phase C.5b — recurrence reset. Creating fresh always starts with
    // Repeat off. Editing an existing occurrence always starts at "This
    // occurrence only" (the safe default) with no pattern change opted
    // into — the coach explicitly chooses a broader apply-to before any
    // recurrence-pattern control even appears.
    setRepeatEnabled(false);
    setRepeatWeekdays([]);
    setRepeatUntilDate('');
    setApplyTo('this');
    setChangePattern(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event, isEditingMatchup]);

  // Auto-check the weekday matching the chosen start date the moment
  // Repeat is turned on (or the date is picked first) — never fights a
  // deliberate manual weekday selection made afterward.
  useEffect(() => {
    if (isCreating && repeatEnabled && form.eventDate && repeatWeekdays.length === 0) {
      const wd = weekdayOfDateString(form.eventDate);
      if (wd !== null) setRepeatWeekdays([wd]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreating, repeatEnabled, form.eventDate]);

  const toggleRepeatWeekday = (idx) => {
    setRepeatWeekdays(ws => (ws.includes(idx) ? ws.filter(w => w !== idx) : [...ws, idx]));
  };

  const toggleSpecificTeam = (teamIdToToggle) => {
    setSpecificTeamIds(ids => (
      ids.includes(teamIdToToggle) ? ids.filter(id => id !== teamIdToToggle) : [...ids, teamIdToToggle]
    ));
  };

  // Cheap, small list, best-effort — a failure here just means the
  // participant pool only ever offers "+ Outside Program" for the
  // second slot, never blocks the rest of the form.
  useEffect(() => {
    if (!open || !isCreating || !soloTeamId) { setSiblingTeams([]); return; }
    let cancelled = false;
    listDivisionSiblingTeams(soloTeamId).then(rows => { if (!cancelled) setSiblingTeams(rows); })
      .catch(() => { if (!cancelled) setSiblingTeams([]); });
    return () => { cancelled = true; };
  }, [open, isCreating, soloTeamId]);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleTypeChange = (_e, type) => {
    if (type === null) return;
    setForm(f => ({ ...f, eventType: type }));
  };

  const swapSlots = () => { setHomeSlot(awaySlot); setAwaySlot(homeSlot); };

  // Phase C.5c — ADD EVENT FORM ORDER. Date/Start/End Time is now one
  // shared block rendered in exactly one of two positions: right after
  // Title for Practice/Other (before Repeat — "that reads backward"
  // otherwise), or after the participants for a Game (unchanged position
  // — this pass never reorders the Game half of the form beyond moving
  // the Swap button, which is a separate, more targeted fix below).
  const renderDateTimeFields = () => (
    <>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth type="date" label={dateFieldLabel} value={form.eventDate} onChange={set('eventDate')}
          InputLabelProps={{ shrink: true }} required onClick={openDatePicker}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TimeSelectField label="Start Time" value={form.startTime} onChange={(v) => setForm(f => ({ ...f, startTime: v }))} required />
      </Grid>
      {/* Phase C.5c — collapsed behind "+ Add End Time" for a fresh
          event with no end time yet (reduces clutter); an existing
          event that already has one shows the field immediately (see
          the reset effect's own setEndTimeExpanded call). */}
      {!isInternalGame && (
        <Grid item xs={12} sm={4}>
          {endTimeExpanded ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  <TimeSelectField label="End Time (optional)" value={form.endTime} onChange={(v) => setForm(f => ({ ...f, endTime: v }))} />
                </Box>
                <Button
                  size="small" onClick={() => { setEndTimeExpanded(false); setForm(f => ({ ...f, endTime: '' })); }}
                  sx={{ textTransform: 'none', color: '#6a8aa8', fontWeight: 600, fontSize: '0.72rem', ml: 0.5 }}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          ) : (
            <Button
              size="small" onClick={() => setEndTimeExpanded(true)}
              sx={{ textTransform: 'none', fontWeight: 600, color: '#6a8aa8' }}
            >
              + Add End Time
            </Button>
          )}
        </Grid>
      )}
    </>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (form.eventType !== 'game' && !form.title.trim()) { setError('Title is required.'); return; }
    if (!form.eventDate) { setError('Date is required.'); return; }
    if (!form.startTime) { setError('Start time is required.'); return; }
    if (!isInternalGame && form.endTime && form.endTime <= form.startTime) { setError('End time must be after start time.'); return; }

    if (form.eventType === 'game' && isCreating) {
      const participantError = validateGameParticipants(homeSlot, awaySlot);
      if (participantError) { setError(participantError); return; }
    }

    // Phase C.5c — Neutral Site UX. Home/Away designation is a separate
    // question from physical venue (a neutral-site game still has real
    // participants) — this only requires that a VENUE decision was made
    // one way or another, never inferring one silently.
    if (form.eventType === 'game') {
      const neutralSiteError = validateNeutralSiteLocation({ isInternalGame, neutralSite, locationName: form.locationName, locationTBD });
      if (neutralSiteError) { setError(neutralSiteError); return; }
    }

    if (form.eventType !== 'game') {
      const scopeError = validateEventScope({ applyScope, specificTeamIds, divisionId, event });
      if (scopeError) { setError(scopeError); return; }
    }

    if (form.eventType !== 'game' && isCreating && repeatEnabled) {
      if (repeatWeekdays.length === 0) { setError('Choose at least one day of the week.'); return; }
      if (!repeatUntilDate) { setError('Choose an until date.'); return; }
      if (repeatUntilDate < form.eventDate) { setError('Until date must be on or after the start date.'); return; }
    }
    if (form.eventType !== 'game' && isRecurringOccurrence && applyTo !== 'this' && changePattern && repeatWeekdays.length === 0) {
      setError('Choose at least one day of the week.');
      return;
    }

    setSaving(true);
    try {
      if (isEditingMatchup) {
        await updateTeamMatchup({
          matchupId: event.id, eventDate: form.eventDate, startTime: form.startTime,
          perspectiveTeamId: event.ownerTeamId,
          homeAway: resolveMatchupEditHomeAway({ ownerTeamId: event.ownerTeamId, homeSlot, neutralSite }),
          notes: form.notes.trim() || null,
        });
      } else if (form.eventType === 'game') {
        const mapped = buildGameParticipantPayload({ homeSlot, awaySlot, neutralSite });
        if (mapped.kind === 'internal') {
          // Known Team vs known Team — always exactly ONE team_matchups
          // row, real Team IDs on both sides, never a name-matched or
          // free-text relationship.
          await createTeamMatchup({
            teamId: mapped.teamId, opponentTeamId: mapped.opponentTeamId, eventDate: form.eventDate,
            startTime: form.startTime, homeAway: mapped.homeAway, notes: form.notes.trim() || null,
          });
        } else {
          const payload = {
            eventType: 'game',
            title: mapped.title,
            eventDate: form.eventDate, startTime: form.startTime, endTime: form.endTime || null,
            opponentName: mapped.opponentName,
            homeAway: mapped.homeAway,
            // Neutral Site UX (C.5c) — an explicit "TBD" is stored as
            // plain text in the same location_name field that already
            // exists (no reusable-Location schema this phase); every
            // other event type never sets it this way.
            locationName: (neutralSite && locationTBD) ? 'TBD' : (form.locationName.trim() || null),
            addressLine1: form.addressLine1.trim() || null,
            city: form.city.trim() || null,
            state: form.state.trim() || null,
            zipCode: form.zipCode.trim() || null,
            notes: form.notes.trim() || null,
          };
          if (event) await updateTeamEvent({ eventId: event.id, ...payload });
          else await createTeamEvent({ teamId: mapped.teamId, ...payload });
        }
      } else if (isCreating && repeatEnabled) {
        // Phase C.5b — a brand-new recurring series. create_recurring_
        // event() (044) creates the event_series template and
        // materializes every matching occurrence in one call.
        const scopePayload = resolveEventScopePayload({ applyScope, specificTeamIds, divisionId, event: null, effectiveTeamId });
        await createRecurringEvent({
          eventType: form.eventType,
          title: form.title.trim(),
          eventDate: form.eventDate,
          startTime: form.startTime,
          untilDate: repeatUntilDate,
          weekdays: repeatWeekdays,
          endTime: form.endTime || null,
          locationName: form.locationName.trim() || null,
          addressLine1: form.addressLine1.trim() || null,
          city: form.city.trim() || null,
          state: form.state.trim() || null,
          zipCode: form.zipCode.trim() || null,
          notes: form.notes.trim() || null,
          ...scopePayload,
        });
      } else if (isRecurringOccurrence && applyTo !== 'this') {
        // Phase C.5b — "This and future" / "Entire series." A weekday
        // pattern change (changePattern) always triggers a series split
        // server-side (044) regardless of which apply-to was chosen;
        // otherwise this is a plain bulk field update across the
        // matching occurrences + the series template.
        const scopePayload = resolveEventScopePayload({ applyScope, specificTeamIds, divisionId, event, effectiveTeamId });
        await updateRecurringSeries({
          eventId: event.id,
          applyTo,
          title: form.title.trim(),
          startTime: form.startTime,
          endTime: form.endTime || null,
          locationName: form.locationName.trim() || null,
          addressLine1: form.addressLine1.trim() || null,
          city: form.city.trim() || null,
          state: form.state.trim() || null,
          zipCode: form.zipCode.trim() || null,
          notes: form.notes.trim() || null,
          ...scopePayload,
          weekdays: changePattern && repeatWeekdays.length > 0 ? repeatWeekdays : null,
        });
      } else {
        // Standalone event, OR "This occurrence only" on a recurring
        // one — a single events row, exactly as before C.5b. Scope is
        // never changed here (see showApplyScope's own guard above).
        const payload = {
          eventType: form.eventType,
          title: form.title.trim(),
          eventDate: form.eventDate,
          startTime: form.startTime,
          endTime: form.endTime || null,
          opponentName: null,
          homeAway: null,
          locationName: form.locationName.trim() || null,
          addressLine1: form.addressLine1.trim() || null,
          city: form.city.trim() || null,
          state: form.state.trim() || null,
          zipCode: form.zipCode.trim() || null,
          notes: form.notes.trim() || null,
        };
        // EVENT SCOPE (043) — resolveEventScopePayload() is driven by
        // applyScope alone, NEVER by showApplyScope (a pure UI-
        // visibility concern). A Team Schedule context never shows the
        // "Applies To" picker, but an inherited entire-Division/
        // specific-Teams event opened there still has applyScope/
        // specificTeamIds correctly preloaded from the event's own
        // current scope (initEventScopeFromExisting(), in the reset
        // effect above) and MUST send that same scope/membership back —
        // otherwise the RPC falls back to the event's existing
        // scope_type server-side with no participant data at all and
        // correctly rejects the save.
        Object.assign(payload, resolveEventScopePayload({ applyScope, specificTeamIds, divisionId, event, effectiveTeamId }));
        if (event) {
          await updateTeamEvent({ eventId: event.id, ...payload });
        } else {
          await createTeamEvent(payload);
        }
      }
      await onSaved();
      onClose();
    } catch (e2) {
      setError(e2.message || 'Could not save this event.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open} onClose={() => { if (!saving) onClose(); }} maxWidth="sm" fullWidth
      PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
    >
      <DialogTitle sx={{ fontWeight: 900 }}>
        {isEditingMatchup ? 'Edit Shared Matchup' : (event ? 'Edit Event' : 'Add Event')}
      </DialogTitle>
      <DialogContent>
        <Box component="form" id="event-form" onSubmit={handleSubmit} sx={{ mt: 0.5 }}>
          {/* Phase C.5b — recurring occurrence edit scope. Only ever
              shown for an EXISTING occurrence that already belongs to a
              series; never for a standalone event, never while
              creating. "This occurrence only" is the safe default —
              Cancel/Postpone/Reschedule-by-editing-date-time all work
              this way without ever seeing this picker at all (this
              dialog itself is only reached for Edit). */}
          {isRecurringOccurrence && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: '1px solid #1e2a3a', backgroundColor: 'rgba(10,20,34,0.5)' }}>
              <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.5 }}>Apply changes to</Typography>
              <RadioGroup value={applyTo} onChange={(e) => setApplyTo(e.target.value)}>
                <FormControlLabel value="this" control={<Radio />} label="This occurrence only" />
                <FormControlLabel value="this_and_future" control={<Radio />} label="This and future occurrences" />
                <FormControlLabel value="series" control={<Radio />} label="Entire series" />
              </RadioGroup>
              {applyTo !== 'this' && (
                <>
                  <FormControlLabel
                    sx={{ mt: 0.5 }}
                    control={<Checkbox checked={changePattern} onChange={e => setChangePattern(e.target.checked)} />}
                    label="Also change which days this repeats on"
                  />
                  {changePattern && (
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.5 }}>On</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {WEEKDAY_LABELS.map((label, idx) => (
                          <Chip
                            key={idx} label={label} clickable
                            color={repeatWeekdays.includes(idx) ? 'primary' : 'default'}
                            onClick={() => toggleRepeatWeekday(idx)}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}

          {!isEditingMatchup && (
            <ToggleButtonGroup value={form.eventType} exclusive onChange={handleTypeChange} size="small" sx={{ mb: 2, flexWrap: 'wrap' }}>
              {Object.keys(EVENT_TYPE_LABELS).map(t => (
                <ToggleButton key={t} value={t} sx={{ textTransform: 'none', fontWeight: 700 }}>{EVENT_TYPE_LABELS[t]}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}

          <Grid container spacing={2}>
            {/* EVENT SCOPE (043) — "Applies To," multi-Team context
                only. Replaces the old single-Team picker: Entire
                Division is the friction-removing default when launched
                from the Division Hub; Specific Teams is real
                event_team_scope membership (checking just one Team is
                still valid, and still honestly Specific-Teams-scoped,
                never silently treated as plain Team scope). */}
            {showApplyScope && (
              <Grid item xs={12}>
                <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.5 }}>Applies To</Typography>
                <RadioGroup
                  value={applyScope}
                  onChange={(e) => setApplyScope(e.target.value)}
                >
                  <FormControlLabel value="entire_division" control={<Radio />} label="Entire Division" />
                  <FormControlLabel value="specific_teams" control={<Radio />} label="Specific Teams" />
                </RadioGroup>
                {applyScope === 'specific_teams' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', pl: 4 }}>
                    {teamOptions.map(t => (
                      <FormControlLabel
                        key={t.id}
                        control={<Checkbox checked={specificTeamIds.includes(t.id)} onChange={() => toggleSpecificTeam(t.id)} />}
                        label={t.team_name}
                      />
                    ))}
                  </Box>
                )}
              </Grid>
            )}

            {form.eventType !== 'game' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Title" value={form.title} onChange={set('title')} required
                  placeholder="Tuesday Practice"
                />
              </Grid>
            )}

            {/* Phase C.5c — ADD EVENT FORM ORDER. Date/Starts/Start
                Time/End Time now comes right after Title — recurrence
                controls used to appear here instead, which read
                backward ("Repeats" before you'd even said when this
                starts). See renderDateTimeFields() above. */}
            {form.eventType !== 'game' && renderDateTimeFields()}

            {/* Phase C.5b — "Repeat this event," Practice/Other CREATE
                only. Editing an existing occurrence never offers this —
                it already either belongs to a series (handled by the
                "Apply changes to" picker above) or is a deliberate
                one-off. */}
            {form.eventType !== 'game' && isCreating && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={repeatEnabled} onChange={e => setRepeatEnabled(e.target.checked)} />}
                  label="Repeat this event"
                />
              </Grid>
            )}
            {/* Phase C.5c — reads "Repeats Weekly / On Tue/Thu / Until
                Oct 15," in that order (previously Until preceded On). */}
            {form.eventType !== 'game' && isCreating && repeatEnabled && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth label="Repeats" value="weekly" disabled>
                    <MenuItem value="weekly">Weekly</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.5 }}>On</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {WEEKDAY_LABELS.map((label, idx) => (
                      <Chip
                        key={idx} label={label} clickable
                        color={repeatWeekdays.includes(idx) ? 'primary' : 'default'}
                        onClick={() => toggleRepeatWeekday(idx)}
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth type="date" label="Until" value={repeatUntilDate} onChange={e => setRepeatUntilDate(e.target.value)}
                    InputLabelProps={{ shrink: true }} onClick={openDatePicker}
                    inputProps={{ min: form.eventDate || undefined, max: maxRecurrenceUntilDate(form.eventDate) || undefined }}
                    required
                  />
                </Grid>
              </>
            )}

            {/* Game participants — known entities only. A coach never
                types a Division Team's name; it's always picked from
                real Team rows. */}
            {form.eventType === 'game' && (
              <>
                <Grid item xs={12}>
                  <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.75 }}>Home Team</Typography>
                  <ParticipantSlot
                    slot={homeSlot} onChange={setHomeSlot}
                    teamPoolOptions={teamPool.filter(t => t.id !== awaySlot?.teamId)}
                    allowExternal={awaySlot?.kind !== 'external'}
                    lockIdentity={!isCreating}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.75 }}>Away Team</Typography>
                  <ParticipantSlot
                    slot={awaySlot} onChange={setAwaySlot}
                    teamPoolOptions={teamPool.filter(t => t.id !== homeSlot?.teamId)}
                    allowExternal={homeSlot?.kind !== 'external'}
                    lockIdentity={!isCreating}
                  />
                </Grid>
                {/* Phase C.5c — SWAP repositioned: it used to sit wedged
                    between the two selectors, reading like a label. It
                    now reads as the clearly secondary action it is,
                    below both Teams. */}
                {isCreating && (
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      size="small" onClick={swapSlots}
                      sx={{ textTransform: 'none', color: '#6a8aa8', fontWeight: 600, fontSize: '0.78rem' }}
                    >
                      ⇄ Swap Home / Away
                    </Button>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox checked={neutralSite} onChange={e => setNeutralSite(e.target.checked)} />}
                    label="Neutral Site"
                  />
                </Grid>
                {isInternalGame && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ py: 0.5 }}>This game will appear on both Teams' schedules.</Alert>
                  </Grid>
                )}
              </>
            )}

            {form.eventType === 'game' && renderDateTimeFields()}

            {/* BROWSER ACCEPTANCE FOLLOW-UP (C.5c) — an internal
                Division matchup (team_matchups, 042/045) still has no
                end time and no location at all — that's not an
                oversight, team_matchups has never modeled a venue —
                but it can now carry an optional operational Notes
                field ("Arrive 30 minutes early," "Field 2"), exactly
                like every other event type already could. Belongs to
                the one shared matchup row, so it's automatically
                consistent from either Team's own Schedule. */}
            {isInternalGame && (
              <Grid item xs={12}>
                <TextField
                  fullWidth multiline minRows={2} label="Notes" value={form.notes} onChange={set('notes')}
                  placeholder="Arrive 30 minutes early. Field 2."
                />
              </Grid>
            )}

            {/* Phase C.5c — Neutral Site UX. Home/Away is a participant
                designation; venue is a separate question, and a neutral-
                site game still needs one — either a real Location, or an
                explicit TBD, never left silently ambiguous. Ordinary
                (non-neutral) games keep the exact same plain optional
                Location this always was. */}
            {!isInternalGame && form.eventType === 'game' && neutralSite && (
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Location (Neutral Site)" value={locationTBD ? '' : form.locationName}
                  onChange={set('locationName')} placeholder="Where will this game be played?"
                  disabled={locationTBD} required={!locationTBD}
                  helperText="Neutral site — enter a venue, or mark it TBD below."
                />
                <FormControlLabel
                  sx={{ mt: 0.25 }}
                  control={
                    <Checkbox
                      checked={locationTBD}
                      onChange={e => { setLocationTBD(e.target.checked); if (e.target.checked) setForm(f => ({ ...f, locationName: '' })); }}
                    />
                  }
                  label="Location TBD"
                />
              </Grid>
            )}

            {!isInternalGame && (
              <>
                {!(form.eventType === 'game' && neutralSite) && (
                  <Grid item xs={12}>
                    <TextField fullWidth label="Location" value={form.locationName} onChange={set('locationName')} placeholder="Jones Dairy Farm Stadium" />
                  </Grid>
                )}
                {!showAddressDetails ? (
                  <Grid item xs={12}>
                    <Button
                      size="small" onClick={() => setShowAddressDetails(true)}
                      sx={{ textTransform: 'none', fontWeight: 600, color: '#6a8aa8', pl: 0 }}
                    >
                      + Add address details
                    </Button>
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Street Address" value={form.addressLine1} onChange={set('addressLine1')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="City" value={form.city} onChange={set('city')} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField fullWidth label="State" value={form.state} onChange={set('state')} inputProps={{ maxLength: 2 }} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField fullWidth label="ZIP code" value={form.zipCode} onChange={set('zipCode')} />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth multiline minRows={2} label="Notes" value={form.notes} onChange={set('notes')}
                    placeholder="Arrive 45 minutes early. Wear white jerseys."
                  />
                </Grid>
              </>
            )}
          </Grid>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: '#9eb8d0' }}>Cancel</Button>
        <Button type="submit" form="event-form" variant="contained" color="primary" disabled={saving} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {saving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : (event ? 'Save Changes' : 'Add Event')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Mark Completed / Edit Score. One shared dialog for both — entering a
 * score on an already-completed game is a correction, not a new action.
 * Uses `event.ownerTeamId` for matchup completion's perspective — every
 * caller's merged item already carries it, so no separate teamId prop
 * is needed here. */
export function CompleteGameDialog({ open, onClose, onSaved, event }) {
  const [teamScore, setTeamScore] = useState('');
  const [opponentScore, setOpponentScore] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && event) {
      setTeamScore(event.team_score !== null && event.team_score !== undefined ? String(event.team_score) : '');
      setOpponentScore(event.opponent_score !== null && event.opponent_score !== undefined ? String(event.opponent_score) : '');
      setError(null);
    }
  }, [open, event]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const ts = parseInt(teamScore, 10);
    const os = parseInt(opponentScore, 10);
    if (!Number.isFinite(ts) || !Number.isFinite(os) || ts < 0 || os < 0) {
      setError('Enter both scores as whole numbers, 0 or higher.');
      return;
    }
    setSaving(true);
    try {
      if (event.kind === 'matchup') {
        await completeTeamMatchup({ matchupId: event.id, perspectiveTeamId: event.ownerTeamId, teamScore: ts, opponentScore: os });
      } else {
        await completeTeamEvent({ eventId: event.id, teamScore: ts, opponentScore: os });
      }
      await onSaved();
      onClose();
    } catch (e2) {
      setError(e2.message || 'Could not save this score.');
    } finally {
      setSaving(false);
    }
  };

  if (!event) return null;

  return (
    <Dialog
      open={open} onClose={() => { if (!saving) onClose(); }} maxWidth="xs" fullWidth
      PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
    >
      <DialogTitle sx={{ fontWeight: 900 }}>{event.status === 'completed' ? 'Edit Result' : 'Enter Result'}</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: '#9eb8d0', mb: 2 }}>vs {event.opponent_name}</Typography>
        <Box component="form" id="complete-game-form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth type="number" label="Team Score" value={teamScore}
                onChange={e => setTeamScore(e.target.value)} inputProps={{ min: 0 }} required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth type="number" label="Opponent Score" value={opponentScore}
                onChange={e => setOpponentScore(e.target.value)} inputProps={{ min: 0 }} required
              />
            </Grid>
          </Grid>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: '#9eb8d0' }}>Cancel</Button>
        <Button type="submit" form="complete-game-form" variant="contained" color="primary" disabled={saving} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {saving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Save Score'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const POSTPONE_TYPE_LABELS = { game: 'Game', practice: 'Practice', other: 'Event' };

/** BROWSER ACCEPTANCE FIX (C.5b) — shared, type-aware Postpone confirm
 * dialog, replacing the two near-identical copies that used to be
 * inlined in TeamSchedulePage.js and DivisionSchedulePage.js — both
 * hard-coded Game wording ("Postpone Game?" / "vs {opponent_name}")
 * even for a Practice/Other, which 044's postpone_team_event() had
 * already been evolved to accept. A Division Matchup is always a Game
 * (list_team_matchups() (042) already normalizes it to
 * `event_type: 'game'` for exactly this kind of shared-UI reuse), so no
 * separate matchup label branch is needed here — only the submit call
 * itself still branches on `kind` to reach the right RPC family.
 * Postpone is, and remains, a single-occurrence action: no "this +
 * future"/"entire series" choice is offered, and postpone_team_event()
 * itself never touches series_id — a recurring occurrence keeps its
 * series membership and its siblings stay untouched.
 *
 * `subjectLine` is optional — TeamSchedulePage.js's own single-Team view
 * never needs a Team-name prefix on a Game (its default below, `vs
 * {opponent_name}`, already matches what it showed before this fix), but
 * DivisionSchedulePage.js's aggregated multi-Team view does, matching
 * the exact `{ownerTeamName} {formatGameOpponentLine}` / `{title}`
 * convention its own Cancel/Delete dialogs already use — so it passes
 * its own precomputed line instead of relying on the default. */
export function PostponeEventDialog({ open, onClose, onSaved, event, subjectLine }) {
  const [postponing, setPostponing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open, event]);

  if (!event) return null;

  const label = POSTPONE_TYPE_LABELS[event.event_type] || 'Event';
  const heading = subjectLine !== undefined
    ? subjectLine
    : (event.event_type === 'game' && event.opponent_name ? `vs ${event.opponent_name}` : event.title);

  const handleConfirm = async () => {
    setError(null);
    setPostponing(true);
    try {
      if (event.kind === 'matchup') {
        await postponeTeamMatchup(event.id);
      } else {
        await postponeTeamEvent(event.id);
      }
      await onSaved();
      onClose();
    } catch (e) {
      setError(e.message || 'Could not postpone this.');
    } finally {
      setPostponing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => { if (!postponing) onClose(); }}
      PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
    >
      <DialogTitle sx={{ fontWeight: 900 }}>Postpone {label}?</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1.5 }}>
          {heading} stays on the Schedule, clearly marked Postponed. Update its date once a new one is known.
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={postponing} sx={{ textTransform: 'none', color: '#9eb8d0' }}>Back</Button>
        <Button onClick={handleConfirm} disabled={postponing} variant="contained" color="secondary" sx={{ textTransform: 'none', fontWeight: 700 }}>
          {postponing ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : `Postpone ${label}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Phase C.5d — REOPEN / CLEAR RESULT confirm dialog, the same shape as
 * PostponeEventDialog just above (optional `subjectLine` override for
 * DivisionHub's own Team-name-prefixed convention). Explicit confirm
 * copy because this clears a real, already-entered score — "The result
 * was entered on the wrong Game / this Game should not be completed,"
 * never a casual undo. Same row/id either way — reopenTeamEvent()/
 * reopenTeamMatchup() (046) only ever update, never insert. */
export function ReopenGameDialog({ open, onClose, onSaved, event, subjectLine }) {
  const [reopening, setReopening] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open, event]);

  if (!event) return null;

  const heading = subjectLine !== undefined
    ? subjectLine
    : (event.event_type === 'game' && event.opponent_name ? `vs ${event.opponent_name}` : event.title);

  const handleConfirm = async () => {
    setError(null);
    setReopening(true);
    try {
      if (event.kind === 'matchup') {
        await reopenTeamMatchup(event.id);
      } else {
        await reopenTeamEvent(event.id);
      }
      await onSaved();
      onClose();
    } catch (e) {
      setError(e.message || 'Could not reopen this Game.');
    } finally {
      setReopening(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => { if (!reopening) onClose(); }}
      PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
    >
      <DialogTitle sx={{ fontWeight: 900 }}>Reopen Game?</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1.5 }}>
          This will clear the final score for {heading} and return it to Scheduled.
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={reopening} sx={{ textTransform: 'none', color: '#9eb8d0' }}>Back</Button>
        <Button onClick={handleConfirm} disabled={reopening} variant="contained" color="secondary" sx={{ textTransform: 'none', fontWeight: 700 }}>
          {reopening ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Reopen Game'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Phase C.5c — EVENT ACTION HIERARCHY. One shared actions row/menu,
 * reused by TeamSchedulePage.js's EventCard and DivisionHub.js's
 * HubEventRow (previously each hand-rolled its own row of buttons,
 * independently, with the exact same gating rules duplicated twice).
 * Primary actions (Edit, Enter/Edit Result) stay directly visible;
 * secondary/destructive ones (Reschedule, Postpone, Cancel, Delete) move
 * behind a "•••" overflow menu — this keeps a manageable row from
 * exposing five buttons at once on a narrow phone screen, on every
 * screen size uniformly (the simplest fix that satisfies both "keep
 * primary actions visible on desktop" and "don't pack tiny links
 * together on mobile" without a separate responsive code path).
 * Reschedule is not a new workflow — it opens the exact same Edit
 * dialog `onEdit` already does; it exists in the menu purely so a
 * coach thinking "I need to reschedule this" has an obviously-labeled
 * place to click, without a second dialog to build or maintain.
 *
 * `event`, `canManage` (already resolved per-row by the caller — this
 * component makes no authority decisions of its own), `canDelete`
 * (separately resolved — TeamSchedulePage's own shared-matchup Delete
 * rule is stricter than its Edit/Cancel rule; DivisionHub's is not).
 * Returns null when the caller has nothing to offer for this row at all
 * (a fully public, unauthenticated viewer).
 *
 * BROWSER ACCEPTANCE FIX (C.5d — historical Game lifecycle) — a
 * completed Game now gets a DELIBERATELY narrower action set than every
 * other status: Reschedule/Postpone/Cancel/Delete all disappear (the
 * database itself now independently rejects Cancel/Delete on a
 * completed row too, 046 — this is UX clarity on top of that, not the
 * only thing preventing it), and the generic "Edit" (date/time/
 * participants/location) is hidden as well — correcting a completed
 * Game's SCORE is "Edit Result"; correcting the fact that it was
 * completed AT ALL is "Reopen Game" (`onReopen`), which must be used
 * before anything else becomes editable again. This is the one status
 * where the overflow menu exists purely to hold that single action.
 *
 * `compact` (C.5d.1) — DivisionHub's own Results ledger (`ResultRow`)
 * needs this same completed-Game action set (Edit Result / Reopen Game,
 * nothing else) but has no room for a separate visible inline button —
 * it's a two-line scoreboard, not a Schedule row. `compact` folds every
 * primary action that would otherwise render as its own inline Button
 * into the "•••" menu instead, so the ONLY visible control is the icon
 * itself. It changes presentation only — every gating rule above is
 * unchanged, and Results only ever hands this a completed row, so in
 * practice `compact` here means exactly "Edit Result + Reopen Game,
 * both inside the menu." */
export function EventActionsMenu({ event, canManage, canDelete, onEdit, onEnterResult, onPostpone, onCancel, onDelete, onReopen, compact }) {
  const [anchorEl, setAnchorEl] = useState(null);
  if (!canManage && !canDelete) return null;

  const isGame = event.event_type === 'game';
  const isCancelled = event.status === 'cancelled';
  const isCompleted = event.status === 'completed';
  const isPostponed = event.status === 'postponed';
  // Mirrors this repo's own pre-existing per-page gating exactly (see
  // this pass's report for the two call sites this replaces) — not a
  // new rule, just centralized.
  const canEnterResult = canManage && isGame && !isCancelled && (isPostponed || event.status === 'scheduled' || isCompleted);
  const canEditGeneric = canManage && !isCompleted;
  const canPostpone = canManage && event.status === 'scheduled';
  const canCancel = canManage && !isCancelled && !isCompleted;
  const canDeleteThis = canDelete && !isCompleted;
  const canReopen = canManage && isCompleted;

  const closeMenu = () => setAnchorEl(null);
  const runAndClose = (fn) => { closeMenu(); fn(event); };

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
      {!compact && canEditGeneric && (
        <Button size="small" onClick={() => onEdit(event)} sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0 }}>Edit</Button>
      )}
      {!compact && canEnterResult && (
        <Button size="small" onClick={() => onEnterResult(event)} sx={{ textTransform: 'none', fontWeight: 700, color: '#2ea84b', minWidth: 0 }}>
          {isCompleted ? 'Edit Result' : 'Enter Result'}
        </Button>
      )}
      <IconButton
        size="small" onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label="More actions" sx={{ color: '#6a8aa8', fontWeight: 900, letterSpacing: '1px' }}
      >
        •••
      </IconButton>
      <Menu
        anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        {compact && canEnterResult && (
          <MenuItem onClick={() => runAndClose(onEnterResult)} sx={{ color: '#2ea84b' }}>
            {isCompleted ? 'Edit Result' : 'Enter Result'}
          </MenuItem>
        )}
        {canReopen && <MenuItem onClick={() => runAndClose(onReopen)} sx={{ color: '#f5a623' }}>Reopen Game</MenuItem>}
        {!compact && canEditGeneric && <MenuItem onClick={() => runAndClose(onEdit)}>Reschedule</MenuItem>}
        {canPostpone && <MenuItem onClick={() => runAndClose(onPostpone)} sx={{ color: '#f5a623' }}>Postpone</MenuItem>}
        {canCancel && <MenuItem onClick={() => runAndClose(onCancel)} sx={{ color: '#e08a4a' }}>Cancel</MenuItem>}
        {(canEditGeneric || canCancel) && canDeleteThis && <Divider />}
        {canDeleteThis && <MenuItem onClick={() => runAndClose(onDelete)} sx={{ color: '#f44336' }}>Delete</MenuItem>}
      </Menu>
    </Box>
  );
}

/** Phase C.5b — the shared "Apply to" 3-way choice for Cancel/Delete on
 * a recurring occurrence, reused identically by TeamSchedulePage.js and
 * DivisionSchedulePage.js's own confirm dialogs (a plain standalone
 * event's Cancel/Delete confirm never renders this at all — its target
 * has no series_id). "This occurrence only" is the safe default. */
export function RecurringApplyToRadios({ value, onChange }) {
  return (
    <RadioGroup value={value} onChange={(e) => onChange(e.target.value)} sx={{ mt: 1, mb: 1 }}>
      <FormControlLabel value="this" control={<Radio size="small" />} label="This occurrence only" />
      <FormControlLabel value="this_and_future" control={<Radio size="small" />} label="This and future occurrences" />
      <FormControlLabel value="series" control={<Radio size="small" />} label="Entire series" />
    </RadioGroup>
  );
}
